import React, {useEffect, useState} from 'react'
import UiInputText from "../components/Form/UiInputText";
import UiInputCheckbox from "../components/Form/UiInputCheckbox";
import UiInputSuggest from "../components/Form/UiInputSuggest";
import UiInputList from "../components/Form/UiInputList";
import {booleanParaEnvio} from "../components/Form/PessoaFields";
import Form from "../components/Form/Form";
import {useForm, FormProvider} from "react-hook-form";
import WindowBody from '../components/Window/WindowBody';
import WindowFooter from '../components/Window/WindowFooter';
import { UiButton } from '../components/UiButton';
import { useNotification } from '../components/Core/NotificationProvider';
import { post } from '../services/ApiService';
import { API_ENDPOINT } from '../components/Core/Grid/gridConstants';

const TIPO_SENHA = 'senha';
const TIPO_TOKEN = 'token';

const tipoList = [
    { id: TIPO_SENHA, title: 'Senha (backoffice)' },
    { id: TIPO_TOKEN, title: 'Token (API)' },
];

/**
 * O `t` é o instante da última mudança do código: sem ele o navegador serviria o
 * PNG que já tem em cache, e o QR gerado agora só apareceria depois de reabrir a
 * tela. Mesmo truque do `DocumentPreviewModal`.
 */
const urlQrCode = (id: any, t: number) =>
    `${API_ENDPOINT}/?route=unusuario/form&action=qrcode`
    + `&rows=${encodeURIComponent(JSON.stringify([{ id }]))}&t=${t}`;

/**
 * O usuário é a conta de acesso, e substituiu a tela de Credenciais.
 *
 * Quem ele é vem do cadastro de Pessoa, que este form referencia pelo suggest em
 * vez de embutir; o que ele pode fazer vem do Perfil, que é o que define o acesso.
 *
 * Duas colunas: os campos à esquerda, o QR à direita. O QR não é campo, é um
 * objeto com estado próprio (existe ou não existe), e as duas ações que o mudam
 * atualizam só a coluna dele, sem fechar a tela nem recarregar o form.
 *
 * A senha só aparece atrás de "Alterar senha?", como no form do vendedor: campo de
 * senha sempre visível convida a sobrescrever sem querer, e em branco o servidor
 * entende "não mexer".
 */
export default function UsuarioForm(props: any){
    const methods = useForm({
        defaultValues: props.data || { ativo: true, tipo: TIPO_SENHA, qrReutilizavel: true }
    });
    const { handleSubmit, reset, watch, getValues, setValue } = methods;
    const notification = useNotification();
    const [qrFalhou, setQrFalhou] = useState(false);
    const [qrToken, setQrToken] = useState(() => Date.now());
    const [ocupado, setOcupado] = useState(false);

    const id = watch('id');
    const tipo = watch('tipo') || TIPO_SENHA;
    const alterarSenha = watch('alterarSenha');
    const codigoQr = watch('codigoQr');
    const senhaDefinida = watch('senhaDefinida');
    const tokenDefinido = watch('tokenDefinido');

    useEffect(() => {
        if (props.data) {
            reset(props.data);
            setQrFalhou(false);
            setQrToken(Date.now());
        }
    }, [props.data, reset]);

    const salvar = (dados: any) => {
        if (!dados.pessoa?.id) {
            notification.error('Usuário precisa da pessoa');
            return;
        }
        if (!dados.perfil?.id) {
            notification.error('Usuário precisa do perfil: é ele que define o acesso');
            return;
        }
        if (dados.tipo === TIPO_SENHA && !String(dados.login || '').trim()) {
            notification.error('Usuário de senha precisa do login');
            return;
        }

        const { alterarSenha: _pedido, senhaDefinida: _s, tokenDefinido: _t, ...envio } = dados;

        props.triggerSubmit({
            ...envio,
            ativo: booleanParaEnvio(dados.ativo),
            qrReutilizavel: booleanParaEnvio(dados.qrReutilizavel),
        });
    };

    /**
     * As duas ações do QR falam com o servidor e escrevem o resultado no próprio
     * form: `codigoQr` é o que decide o estado do bloco, então trocar o valor já
     * troca a coluna da direita. Nada de fechar o modal nem recarregar o form.
     */
    const acaoDoQr = async (
        action: 'gerarQr' | 'revogarQr',
        carregando: string,
        erro: string
    ) => {
        const vals = getValues();
        if (!vals.id) {
            notification.error('Salve o usuário antes de mexer no QR');
            return;
        }

        setOcupado(true);
        const loadingId = notification.showLoading(carregando);
        try {
            const rows = encodeURIComponent(JSON.stringify([{ id: vals.id }]));
            const response = await post(`/?route=unusuario/form&action=${action}&rows=${rows}`, {
                dados: { id: vals.id }
            });
            notification.hideLoading(loadingId);

            if (response.data?.success) {
                setValue('codigoQr', response.data?.codigoQr ?? null);
                setQrFalhou(false);
                setQrToken(Date.now());
                notification.success(response.data?.message || 'Pronto');
            } else {
                notification.error(response.data?.message || erro);
            }
        } catch {
            notification.hideLoading(loadingId);
            notification.error('Erro de comunicação com o servidor');
        } finally {
            setOcupado(false);
        }
    };

    const gerarQr = () => acaoDoQr('gerarQr', 'Gerando o QR...', 'Erro ao gerar o QR');

    const revogarQr = async () => {
        const confirmado = await notification.confirm(
            'Revogar DESCONECTA TODOS os aparelhos deste usuário, de uma vez, e o deixa sem ' +
            'código. Quem estiver com o QR antigo em campo perde o acesso. Gerar um código ' +
            'novo é outra ação, depois. Confirma?'
        );
        if (!confirmado) return;

        return acaoDoQr('revogarQr', 'Revogando...', 'Erro ao revogar o QR');
    };

    return (
        <FormProvider {...methods}>
            <Form onSubmit={handleSubmit(salvar)} methods={methods}>
                <WindowBody>
                    <div className="row">
                        <div className="col-lg-7">
                            <div className="row">
                                <UiInputSuggest size={12} name="pessoa" router="unpessoa" idField="id" titleField="nome" label="Pessoa"/>

                                <UiInputSuggest size={7} name="perfil" router="unperfil" idField="id" titleField="nome" label="Perfil de acesso"/>
                                <UiInputList size={5} name="tipo" list={tipoList} idField="id" titleField="title" label="Tipo"/>

                                <UiInputText mask={"lowercase"} size={12} name="login" label="Login"/>

                                <UiInputCheckbox size={6} name="qrReutilizavel" label="QR reutilizável"/>
                                <UiInputCheckbox size={6} name="ativo" label="Ativo"/>

                                {tipo === TIPO_SENHA &&
                                    <>
                                        <UiInputCheckbox size={5} name="alterarSenha"
                                            label={senhaDefinida ? 'Alterar senha?' : 'Definir senha?'}/>
                                        { alterarSenha === true && <UiInputText size={7} name="senha" label="Nova senha"/> }
                                    </>
                                }

                                {tipo === TIPO_TOKEN &&
                                    <UiInputText size={12} name="token"
                                        label={tokenDefinido ? 'Novo token (só o hash é guardado)' : 'Token da integração'}/>
                                }
                            </div>
                        </div>

                        <div className="col-lg-5">
                            <fieldset>
                                <legend>QR de pareamento do app</legend>

                                {!id &&
                                    <p className="text-muted small">
                                        Salve o usuário primeiro. O QR é gerado contra um usuário que já existe.
                                    </p>
                                }

                                {id && codigoQr &&
                                    <div style={{ textAlign: 'center' }}>
                                        {qrFalhou
                                            ? <span className="text-muted small">QR indisponível</span>
                                            : <img
                                                src={urlQrCode(id, qrToken)}
                                                alt="QR de pareamento do usuário"
                                                onError={() => setQrFalhou(true)}
                                                style={{ width: 190, height: 190, imageRendering: 'pixelated', background: '#FFF' }}
                                            />
                                        }
                                        <div style={{ fontFamily: 'monospace', letterSpacing: 1, margin: '8px 0 12px' }}>
                                            {codigoQr}
                                        </div>
                                        <UiButton variant="danger" type="button" onClick={revogarQr} disabled={ocupado}>
                                            Revogar e desconectar aparelhos
                                        </UiButton>
                                    </div>
                                }

                                {id && !codigoQr &&
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{
                                            width: 190,
                                            height: 190,
                                            margin: '0 auto',
                                            border: '1px dashed #c3ccd5',
                                            borderRadius: 6,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                            <span className="text-muted small">Sem QR gerado</span>
                                        </div>
                                        <div style={{ margin: '12px 0' }}>
                                            <UiButton variant="primary" type="button" onClick={gerarQr} disabled={ocupado}>
                                                Gerar QR
                                            </UiButton>
                                        </div>
                                    </div>
                                }
                            </fieldset>
                        </div>
                    </div>
                </WindowBody>

                <WindowFooter>
                    <UiButton variant="secondary" onClick={props.triggerClose}>Fechar</UiButton>
                    <UiButton variant="primary" type="submit">Salvar</UiButton>
                </WindowFooter>
            </Form>
        </FormProvider>
    )
}
